import { createWebHistory, createRouter } from "vue-router"
import routes from "./routes"
import appConfig from "../../app.config"
import { useAuthStore } from "@/store/auth"

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0, left: 0 }
  },
})

// Before each route evaluates...

router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()

  if (to.meta?.authRequired) {
    if (!authStore.loggedIn) {
      if (authStore.user.refreshToken) {
        // If refresh token exists, allow access
        // The axios interceptor will refresh the token when needed
        return next()
      }
      return next({ name: "login" })
    }
  }

  if (to.meta?.isAdmin && !authStore.isAdmin) {
    return next({ name: "forbidden" })
  }

  next()
})

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args)
              reject(new Error("Redirected"))
            } else {
              resolve()
            }
          })
        } else {
          // Otherwise, continue resolving the route.
          resolve()
        }
      })
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return
  }
  if (routeTo.meta?.title) {
    document.title = `${routeTo.meta.title} | ${appConfig.title}`
  }
  // If we reach this point, continue resolving the route.
  next()
})

export default router
